import React, { useState } from 'react';
import { Trophy, Users, Star, TrendingUp, Quote, X, Calendar, Play } from 'lucide-react';
import { cricketLegends, womenCricketPathway, internationalMoments } from '../data/cricketLegends';

const LegendsGallery = () => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedMoment, setSelectedMoment] = useState(null);
  const [filter, setFilter] = useState('all');
  const [momentFilter, setMomentFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('legends');

  const filteredLegends = filter === 'all' 
    ? cricketLegends 
    : filter === 'women' 
      ? cricketLegends.filter(p => p.gender === 'Female')
      : cricketLegends.filter(p => p.gender !== 'Female');

  const filteredMoments = momentFilter === 'all'
    ? internationalMoments
    : internationalMoments.filter(m => m.format === momentFilter);

  return (
    <section id="gallery" className="bg-white py-12 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black tracking-[0.05em] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Cricket Hall of Fame
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Celebrating legends, iconic moments, and the journeys that inspire millions
          </p>
        </div>

        {/* Section Toggle */}
        <div className="flex justify-center gap-2 sm:gap-4 mb-8 sm:mb-12 flex-wrap">
          <button
            onClick={() => setActiveSection('legends')}
            className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
              activeSection === 'legends' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="inline mr-1 sm:mr-2" size={16} />
            Legends
          </button>
          <button
            onClick={() => setActiveSection('moments')}
            className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
              activeSection === 'moments' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Trophy className="inline mr-2" size={18} />
            Iconic Moments
          </button>
        </div>

        {/* LEGENDS SECTION */}
        {activeSection === 'legends' && (
          <>
            {/* Filter Tabs */}
            <div className="flex justify-center gap-4 mb-12">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === 'all' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Legends
              </button>
              <button
                onClick={() => setFilter('men')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === 'men' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Men's Cricket
              </button>
              <button
                onClick={() => setFilter('women')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === 'women' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Women's Cricket
              </button>
            </div>

            {/* Legends Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredLegends.map((player) => (
                <div
                  key={player.id}
                  onClick={() => setSelectedPlayer(player)}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-square overflow-hidden rounded-lg mb-3 bg-gray-100">
                    <img
                      src={player.image}
                      alt={player.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${player.name}&size=320&background=000&color=fff&bold=true`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-white text-xs">{player.role}</p>
                        <p className="text-yellow-400 text-xs mt-1">{player.nickname}</p>
                      </div>
                    </div>
                    {player.retired && (
                      <div className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded">
                        Legend
                      </div>
                    )}
                    {player.gender === 'Female' && (
                      <div className="absolute top-2 left-2 bg-pink-500 text-white text-xs px-2 py-1 rounded">
                        Women
                      </div>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 group-hover:text-black transition-colors">
                    {player.name}
                  </h3>
                  <p className="text-sm text-gray-500">{player.country}</p>
                </div>
              ))}
            </div>

            {/* Women's Cricket Pathway Section */}
            <div className="mt-20 bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8">
              <h3 className="text-3xl font-bold text-center mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                Women's Cricket Pathway
              </h3>
              <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
                The path to representing India in women's cricket is now clearer than ever. Here's your roadmap to success.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {womenCricketPathway.levels.map((level, index) => (
                  <div key={index} className="bg-white rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <h4 className="font-bold text-gray-900">{level.level}</h4>
                        <p className="text-xs text-gray-500">{level.age}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{level.description}</p>
                    <div className="space-y-1">
                      {level.opportunities.map((opp, i) => (
                        <p key={i} className="text-xs text-pink-600">• {opp}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ICONIC MOMENTS SECTION */}
        {activeSection === 'moments' && (
          <>
            {/* Format Filter */}
            <div className="flex justify-center flex-wrap gap-3 mb-12">
              {['all', 'Test', 'ODI', 'T20I', 'T20'].map((format) => (
                <button
                  key={format}
                  onClick={() => setMomentFilter(format)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    momentFilter === format 
                      ? format === 'Test' ? 'bg-red-500 text-white'
                        : format === 'ODI' ? 'bg-blue-500 text-white'
                        : format === 'T20I' || format === 'T20' ? 'bg-green-500 text-white'
                        : 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {format === 'all' ? 'All Formats' : format}
                </button>
              ))}
            </div>

            {/* Moments Count */}
            <p className="text-center text-gray-500 mb-8">
              Showing {filteredMoments.length} iconic moments
            </p>

            {/* Moments Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMoments.map((moment) => (
                <div
                  key={moment.id}
                  onClick={() => setSelectedMoment(moment)}
                  className="group cursor-pointer bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={moment.image}
                      alt={moment.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = `https://placehold.co/720x400/1a1a1a/ffffff?text=${encodeURIComponent(moment.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        moment.format === 'Test' ? 'bg-red-500 text-white'
                        : moment.format === 'ODI' ? 'bg-blue-500 text-white'
                        : 'bg-green-500 text-white'
                      }`}>
                        {moment.format}
                      </span>
                      <span className="bg-black/60 text-white px-2 py-1 rounded text-xs">
                        {moment.year}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-white/80 text-xs">{moment.event}</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-black">
                      {moment.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {moment.description}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {moment.players.slice(0, 3).map((player, i) => (
                        <span key={i} className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs">
                          {player}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Player Detail Modal */}
      {selectedPlayer && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedPlayer(null)}
        >
          <div
            className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Image */}
            <div className="relative h-64 md:h-80 bg-gradient-to-br from-gray-900 to-black">
              <button
                onClick={() => setSelectedPlayer(null)}
                className="absolute top-4 right-4 bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-10"
              >
                <X size={24} />
              </button>
              <div className="absolute inset-0 flex items-end">
                <div className="w-full p-6 md:p-8 flex items-end gap-6">
                  <img
                    src={selectedPlayer.image}
                    alt={selectedPlayer.name}
                    className="w-32 h-32 md:w-40 md:h-40 rounded-xl object-cover border-4 border-white shadow-xl"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${selectedPlayer.name}&size=320&background=000&color=fff&bold=true`;
                    }}
                  />
                  <div className="text-white pb-2">
                    <p className="text-yellow-400 text-sm font-medium">{selectedPlayer.nickname}</p>
                    <h2 className="text-3xl md:text-4xl font-bold">{selectedPlayer.name}</h2>
                    <p className="text-gray-300">{selectedPlayer.role} • {selectedPlayer.country}</p>
                    {selectedPlayer.retired && (
                      <span className="inline-block mt-2 bg-yellow-500 text-black text-xs px-3 py-1 rounded-full font-medium">
                        Retired Legend
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Quote */}
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <Quote className="text-gray-300 mb-2" size={32} />
                <p className="text-xl italic text-gray-700">{selectedPlayer.quote}</p>
              </div>

              {/* Journey Story */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Star className="text-yellow-500" size={24} />
                  The Journey
                </h3>
                <p className="text-gray-600 leading-relaxed">{selectedPlayer.journey}</p>
              </div>

              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-5">
                  <h4 className="font-bold text-gray-900 mb-3">Personal Details</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Born:</span> {selectedPlayer.born}</p>
                    <p><span className="text-gray-500">Birthplace:</span> {selectedPlayer.birthplace}</p>
                    <p><span className="text-gray-500">Family:</span> {selectedPlayer.family}</p>
                    <p><span className="text-gray-500">Net Worth:</span> <span className="text-green-600 font-medium">{selectedPlayer.netWorth}</span></p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-5">
                  <h4 className="font-bold text-gray-900 mb-3">Debut</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Test:</span> {selectedPlayer.debut.test}</p>
                    <p><span className="text-gray-500">ODI:</span> {selectedPlayer.debut.odi}</p>
                    <p><span className="text-gray-500">T20I:</span> {selectedPlayer.debut.t20i}</p>
                  </div>
                </div>
              </div>

              {/* Career Stats */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="text-blue-500" size={24} />
                  Career Statistics
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {selectedPlayer.stats.tests && selectedPlayer.stats.tests.matches > 0 && (
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-red-600 font-medium mb-1">TESTS</p>
                      <p className="text-2xl font-bold text-gray-900">{selectedPlayer.stats.tests.matches}</p>
                      <p className="text-xs text-gray-500">matches</p>
                      <div className="mt-2 text-sm">
                        <p><span className="text-gray-500">Runs:</span> {selectedPlayer.stats.tests.runs}</p>
                        {selectedPlayer.stats.tests.wickets && (
                          <p><span className="text-gray-500">Wickets:</span> {selectedPlayer.stats.tests.wickets}</p>
                        )}
                        {selectedPlayer.stats.tests.centuries !== undefined && (
                          <p><span className="text-gray-500">100s:</span> {selectedPlayer.stats.tests.centuries}</p>
                        )}
                        <p><span className="text-gray-500">Avg:</span> {selectedPlayer.stats.tests.average}</p>
                      </div>
                    </div>
                  )}
                  {selectedPlayer.stats.odis && selectedPlayer.stats.odis.matches > 0 && (
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-blue-600 font-medium mb-1">ODIs</p>
                      <p className="text-2xl font-bold text-gray-900">{selectedPlayer.stats.odis.matches}</p>
                      <p className="text-xs text-gray-500">matches</p>
                      <div className="mt-2 text-sm">
                        <p><span className="text-gray-500">Runs:</span> {selectedPlayer.stats.odis.runs || '-'}</p>
                        {selectedPlayer.stats.odis.wickets && (
                          <p><span className="text-gray-500">Wickets:</span> {selectedPlayer.stats.odis.wickets}</p>
                        )}
                        {selectedPlayer.stats.odis.centuries !== undefined && (
                          <p><span className="text-gray-500">100s:</span> {selectedPlayer.stats.odis.centuries}</p>
                        )}
                        <p><span className="text-gray-500">Avg:</span> {selectedPlayer.stats.odis.average}</p>
                      </div>
                    </div>
                  )}
                  {selectedPlayer.stats.t20is && selectedPlayer.stats.t20is.matches > 0 && (
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-green-600 font-medium mb-1">T20Is</p>
                      <p className="text-2xl font-bold text-gray-900">{selectedPlayer.stats.t20is.matches}</p>
                      <p className="text-xs text-gray-500">matches</p>
                      <div className="mt-2 text-sm">
                        <p><span className="text-gray-500">Runs:</span> {selectedPlayer.stats.t20is.runs || '-'}</p>
                        {selectedPlayer.stats.t20is.wickets && (
                          <p><span className="text-gray-500">Wickets:</span> {selectedPlayer.stats.t20is.wickets}</p>
                        )}
                        {selectedPlayer.stats.t20is.centuries !== undefined && (
                          <p><span className="text-gray-500">100s:</span> {selectedPlayer.stats.t20is.centuries}</p>
                        )}
                        <p><span className="text-gray-500">Avg:</span> {selectedPlayer.stats.t20is.average}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Records */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Trophy className="text-yellow-500" size={24} />
                  Major Records
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {selectedPlayer.records.map((record, index) => (
                    <div key={index} className="flex items-start gap-2 bg-yellow-50 rounded-lg p-3">
                      <span className="text-yellow-500">★</span>
                      <p className="text-sm text-gray-700">{record}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Achievements */}
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Users className="text-purple-500" size={24} />
                  Achievements & Awards
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedPlayer.achievements.map((achievement, index) => (
                    <span
                      key={index}
                      className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium"
                    >
                      {achievement}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Moment Detail Modal */}
      {selectedMoment && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedMoment(null)}
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 md:h-80">
              <img
                src={selectedMoment.image}
                alt={selectedMoment.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = `https://placehold.co/720x400/1a1a1a/ffffff?text=${encodeURIComponent(selectedMoment.title)}`;
                }}
              />
              <button
                onClick={() => setSelectedMoment(null)}
                className="absolute top-4 right-4 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black transition-colors"
              >
                <X size={24} />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                <div className="flex gap-2 mb-2">
                  <span className={`px-3 py-1 rounded text-xs font-medium ${
                    selectedMoment.format === 'Test' ? 'bg-red-500 text-white'
                    : selectedMoment.format === 'ODI' ? 'bg-blue-500 text-white'
                    : 'bg-green-500 text-white'
                  }`}>
                    {selectedMoment.format}
                  </span>
                  <span className="bg-white/20 text-white px-3 py-1 rounded text-xs">
                    {selectedMoment.year}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{selectedMoment.title}</h2>
                <p className="text-white/80 text-sm mt-1">{selectedMoment.event}</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {selectedMoment.description}
              </p>
              <div>
                <h4 className="font-bold text-gray-900 mb-3">Key Players</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedMoment.players.map((player, i) => (
                    <span key={i} className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium">
                      {player}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default LegendsGallery;
